import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbProps {
  items?: BreadcrumbItem[];
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ items }) => {
  const location = useLocation();

  // Generate breadcrumb from current path if no items provided
  const generateBreadcrumb = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumb: BreadcrumbItem[] = [
      { label: 'Início', href: '/' }
    ];

    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;

      // Map segment to readable label
      const labelMap: { [key: string]: string } = {
        'blog': 'Blog',
        'calculadoras': 'Calculadoras',
        'planilhas': 'Planilhas',
        'investimentos': 'Investimentos',
        'dividas': 'Dívidas',
        'economia': 'Economia',
        'pix': 'PIX',
        'cartao-credito': 'Cartão de Crédito',
        'bancos-digitais': 'Bancos Digitais',
        'criptomoedas': 'Criptomoedas',
        'fiis': 'FIIs',
        'tesouro-direto': 'Tesouro Direto',
        'financiamento': 'Financiamento',
        'aluguel-vs-compra': 'Aluguel vs Compra',
        'imposto-renda': 'Imposto de Renda',
        'isencoes': 'Isenções',
        'mercado': 'Mercado'
      };

      const label = labelMap[segment] || segment.charAt(0).toUpperCase() + segment.slice(1);

      // Don't add href for the last item (current page)
      const href = index === pathSegments.length - 1 ? undefined : currentPath;

      breadcrumb.push({ label, href });
    });

    return breadcrumb;
  };

  const breadcrumbItems = items || generateBreadcrumb();

  // Don't show breadcrumb on home page
  if (location.pathname === '/') {
    return null;
  }

  return (
    <nav className="bg-gray-50 border-b border-gray-200" aria-label="Breadcrumb">
      {/* BreadcrumbList Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": breadcrumbItems.map((item, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "name": item.label,
              ...(item.href ? { "item": `https://oteudinheiro.com.br${item.href}` } : {})
            }))
          })
        }}
      />
      <div className="container-custom py-3">
        <ol className="flex items-center space-x-2 text-sm">
          {breadcrumbItems.map((item, index) => (
            <li key={index} className="flex items-center">
              {index > 0 && (
                <ChevronRight className="w-4 h-4 text-gray-400 mx-2" />
              )}

              {index === 0 && (
                <Home className="w-4 h-4 text-gray-500 mr-1" />
              )}

              {item.href ? (
                <Link
                  to={item.href}
                  className="text-gray-600 hover:text-primary-600 transition-colors"
                >
                  {item.label}
                </Link>
              ) : (
                <span className="text-gray-900 font-medium" aria-current="page">
                  {item.label}
                </span>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
};

export default Breadcrumb;
